import React from 'react';
import { Straighten, AttachMoney, Inventory, ExpandMore, ExpandLess } from '@mui/icons-material';
import { FormControl, Select, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  useProductPropertiesStep,
  ProductPropertiesStepProps,
} from './ProductPropertiesStep.hooks';
import { APP_CURRENCY } from '../../../../utils/constants/currency';
import { FlagSwitch } from '../../../../components/ui';
import { useLocaleDecimalInput } from '../../../../hooks/useLocaleDecimalInput';
import {
  Container,
  EmptyState,
  EmptyStateText,
  SummaryInfo,
  SummaryTitle,
  SummaryDescription,
  SummaryStats,
  TabsContainer,
  TabButton,
  TabIcon,
  ProductGroupsContainer,
  ProductCard,
  ProductHeader,
  ProductHeaderContent,
  ProductInfo,
  ProductIcon,
  ProductDetails,
  ProductName,
  ProductCode,
  ProductActions,
  ResetButton,
  ExpandIcon,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  HeaderCell,
  HeaderCellWithActions,
  TableBody,
  BodyCell,
  InputField,
  SelectField,
  inheritedSelectSx,
} from './ProductPropertiesStep.styles';

const DecimalInputField: React.FC<{
  value: string | number;
  onChange: (value: string) => void;
  decimalPlaces?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
}> = ({ value, onChange, decimalPlaces = 2, min, max, disabled }) => {
  const {
    displayValue,
    onFocus,
    onBlur,
    onChange: handleChange,
  } = useLocaleDecimalInput({
    value,
    onChange: (val) => onChange(val != null ? String(val) : ''),
    decimalPlaces,
    min,
    max,
  });

  return (
    <InputField
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled}
    />
  );
};

const ProductPropertiesStep: React.FC<ProductPropertiesStepProps> = (props) => {
  const theme = useTheme();
  const {
    // State
    activeTab,
    setActiveTab,
    units,
    productGroups,
    materials,
    colors,
    expandedProducts,
    editableColumns,

    // Functions
    toggleProductExpansion,
    toggleColumnEditable,
    resetToParentDefaults,
    handleUnitChange,
    calculateMargin,
    getGlobalUnitIndex,
  } = useProductPropertiesStep(props);

  if (units.length === 0) {
    return (
      <EmptyState>
        <EmptyStateText>No hay piezas para configurar.</EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <Container>
      {/* Información resumen */}
      <SummaryInfo>
        <SummaryTitle>Configuración de Propiedades y Precios</SummaryTitle>
        <SummaryDescription>
          Configure las propiedades físicas y costos de cada pieza. Las piezas se inicializan con
          valores predeterminados del producto padre.
        </SummaryDescription>
        <SummaryStats>
          Total de productos: {productGroups.length} | Total de piezas: {units.length}
        </SummaryStats>
      </SummaryInfo>

      {/* Tabs */}
      <TabsContainer>
        <TabButton active={activeTab === 'properties'} onClick={() => setActiveTab('properties')}>
          <TabIcon>
            <Straighten style={{ fontSize: 16 }} />
          </TabIcon>
          Propiedades
        </TabButton>
        <TabButton active={activeTab === 'costs'} onClick={() => setActiveTab('costs')}>
          <TabIcon>
            <AttachMoney style={{ fontSize: 16 }} />
          </TabIcon>
          Precios
        </TabButton>
      </TabsContainer>

      {/* Content grouped by products */}
      <ProductGroupsContainer>
        {productGroups.map((group, groupIndex) => (
          <ProductCard key={group.productCode}>
            {/* Product Header - Clickable */}
            <ProductHeader
              gradientType={groupIndex % 2 === 0 ? 'blue' : 'green'}
              onClick={() => toggleProductExpansion(group.productCode)}
            >
              <ProductHeaderContent>
                <ProductInfo>
                  <ProductIcon colorType={groupIndex % 2 === 0 ? 'blue' : 'green'}>
                    <Inventory style={{ fontSize: 20 }} />
                  </ProductIcon>
                  <ProductDetails>
                    <ProductName>{group.productName}</ProductName>
                    <ProductCode>
                      Código: {group.productCode} • {group.totalQuantity}{' '}
                      {group.totalQuantity === 1 ? 'pieza' : 'piezas'}
                    </ProductCode>
                  </ProductDetails>
                </ProductInfo>
                <ProductActions>
                  {/* Reset to Parent Defaults Button */}
                  <ResetButton
                    colorType={groupIndex % 2 === 0 ? 'blue' : 'green'}
                    onClick={(e) => {
                      e.stopPropagation();
                      resetToParentDefaults(group.productCode);
                    }}
                    title="Restablecer a valores por defecto del producto padre"
                  >
                    Restablecer
                  </ResetButton>

                  {/* Expand/Collapse Arrow */}
                  <ExpandIcon colorType={groupIndex % 2 === 0 ? 'blue' : 'green'}>
                    {expandedProducts[group.productCode] ? (
                      <ExpandLess style={{ fontSize: 24 }} />
                    ) : (
                      <ExpandMore style={{ fontSize: 24 }} />
                    )}
                  </ExpandIcon>
                </ProductActions>
              </ProductHeaderContent>
            </ProductHeader>

            {/* Collapsible Content */}
            {expandedProducts[group.productCode] && (
              <>
                {/* Pestaña de propiedades */}
                {activeTab === 'properties' && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <HeaderCell style={{ width: '38px', textAlign: 'center' }}>#</HeaderCell>
                          <HeaderCellWithActions style={{ width: '92px' }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>Material</span>
                              <FlagSwitch
                                checked={editableColumns[group.productCode]?.materialId ?? false}
                                onChange={(e, checked) => {
                                  if (
                                    checked !==
                                    (editableColumns[group.productCode]?.materialId ?? false)
                                  ) {
                                    toggleColumnEditable(group.productCode, 'materialId');
                                  }
                                }}
                              />
                            </div>
                          </HeaderCellWithActions>
                          <HeaderCellWithActions style={{ width: '80px' }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>Peso (G)</span>
                              <FlagSwitch
                                checked={editableColumns[group.productCode]?.weight ?? false}
                                onChange={(e, checked) => {
                                  if (
                                    checked !==
                                    (editableColumns[group.productCode]?.weight ?? false)
                                  ) {
                                    toggleColumnEditable(group.productCode, 'weight');
                                  }
                                }}
                              />
                            </div>
                          </HeaderCellWithActions>
                          <HeaderCellWithActions style={{ width: '80px' }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>Largo (cm)</span>
                              <FlagSwitch
                                checked={editableColumns[group.productCode]?.length ?? false}
                                onChange={(e, checked) => {
                                  if (
                                    checked !==
                                    (editableColumns[group.productCode]?.length ?? false)
                                  ) {
                                    toggleColumnEditable(group.productCode, 'length');
                                  }
                                }}
                              />
                            </div>
                          </HeaderCellWithActions>
                          <HeaderCellWithActions style={{ width: '80px' }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>Talla</span>
                              <FlagSwitch
                                checked={editableColumns[group.productCode]?.diameter ?? false}
                                onChange={(e, checked) => {
                                  if (
                                    checked !==
                                    (editableColumns[group.productCode]?.diameter ?? false)
                                  ) {
                                    toggleColumnEditable(group.productCode, 'diameter');
                                  }
                                }}
                              />
                            </div>
                          </HeaderCellWithActions>
                          <HeaderCellWithActions style={{ width: '100px' }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>Color</span>
                              <FlagSwitch
                                checked={editableColumns[group.productCode]?.color ?? false}
                                onChange={(e, checked) => {
                                  if (
                                    checked !== (editableColumns[group.productCode]?.color ?? false)
                                  ) {
                                    toggleColumnEditable(group.productCode, 'color');
                                  }
                                }}
                              />
                            </div>
                          </HeaderCellWithActions>
                          <HeaderCellWithActions style={{ width: '100px' }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <span>Código Proveedor</span>
                              <FlagSwitch
                                checked={editableColumns[group.productCode]?.workshopCode ?? false}
                                onChange={(e, checked) => {
                                  if (
                                    checked !==
                                    (editableColumns[group.productCode]?.workshopCode ?? false)
                                  ) {
                                    toggleColumnEditable(group.productCode, 'workshopCode');
                                  }
                                }}
                              />
                            </div>
                          </HeaderCellWithActions>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.units.map((unit, unitIndex) => {
                          const globalIndex = getGlobalUnitIndex(unit);
                          return (
                            <TableRow key={unit.id}>
                              <BodyCell
                                style={{
                                  width: '38px',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                }}
                              >
                                {unitIndex + 1}
                              </BodyCell>
                              <BodyCell style={{ width: '92px', maxWidth: '92px' }}>
                                <SelectField>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{ minWidth: 0, maxWidth: '100%' }}
                                  >
                                    <Select
                                      value={String(unit.properties.materialId ?? '')}
                                      onChange={(e) =>
                                        handleUnitChange(
                                          globalIndex,
                                          'properties',
                                          'materialId',
                                          e.target.value as string,
                                        )
                                      }
                                      displayEmpty
                                      size="small"
                                      disabled={
                                        !(editableColumns[unit.productCode]?.materialId ?? false)
                                      }
                                      sx={{
                                        fontSize: '0.8rem',
                                        '& .MuiSelect-select': {
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          py: 0.75,
                                        },
                                        ...(!(
                                          editableColumns[unit.productCode]?.materialId ?? false
                                        )
                                          ? inheritedSelectSx(theme)
                                          : {}),
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>Selecciona un material</em>
                                      </MenuItem>
                                      {materials.map((material) => (
                                        <MenuItem key={material.id} value={material.id.toString()}>
                                          {material.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </SelectField>
                              </BodyCell>
                              <BodyCell style={{ width: '80px', maxWidth: '80px' }}>
                                <DecimalInputField
                                  value={unit.properties.weight}
                                  onChange={(nextValue) =>
                                    handleUnitChange(globalIndex, 'properties', 'weight', nextValue)
                                  }
                                  min={0}
                                  disabled={!(editableColumns[unit.productCode]?.weight ?? false)}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '80px', maxWidth: '80px' }}>
                                <DecimalInputField
                                  value={unit.properties.length}
                                  onChange={(nextValue) =>
                                    handleUnitChange(globalIndex, 'properties', 'length', nextValue)
                                  }
                                  min={0}
                                  disabled={!(editableColumns[unit.productCode]?.length ?? false)}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '80px', maxWidth: '80px' }}>
                                <DecimalInputField
                                  value={unit.properties.diameter}
                                  onChange={(nextValue) =>
                                    handleUnitChange(
                                      globalIndex,
                                      'properties',
                                      'diameter',
                                      nextValue,
                                    )
                                  }
                                  min={0}
                                  disabled={!(editableColumns[unit.productCode]?.diameter ?? false)}
                                />
                              </BodyCell>
                              <BodyCell style={{ width: '100px', maxWidth: '100px' }}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={unit.properties.color}
                                    displayEmpty
                                    onChange={(e) =>
                                      handleUnitChange(
                                        globalIndex,
                                        'properties',
                                        'color',
                                        e.target.value,
                                      )
                                    }
                                    disabled={!(editableColumns[unit.productCode]?.color ?? false)}
                                    sx={{
                                      ...(!(editableColumns[unit.productCode]?.color ?? false)
                                        ? inheritedSelectSx(theme)
                                        : {}),
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Selecciona un color</em>
                                    </MenuItem>
                                    {colors.map((color) => (
                                      <MenuItem key={color.id} value={color.name}>
                                        {color.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </BodyCell>
                              <BodyCell style={{ width: '100px', maxWidth: '100px' }}>
                                <InputField
                                  type="text"
                                  value={unit.properties.workshopCode}
                                  onChange={(e) =>
                                    handleUnitChange(
                                      globalIndex,
                                      'properties',
                                      'workshopCode',
                                      e.target.value,
                                    )
                                  }
                                  disabled={
                                    !(editableColumns[unit.productCode]?.workshopCode ?? false)
                                  }
                                />
                              </BodyCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Pestaña de costos */}
                {activeTab === 'costs' && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <HeaderCell style={{ width: '38px', textAlign: 'center' }}>#</HeaderCell>
                          <HeaderCellWithActions>
                            Costo Unitario ({APP_CURRENCY.symbol})
                            <FlagSwitch
                              checked={editableColumns[group.productCode]?.costPrice ?? false}
                              onChange={(e, checked) => {
                                if (
                                  checked !==
                                  (editableColumns[group.productCode]?.costPrice ?? false)
                                ) {
                                  toggleColumnEditable(group.productCode, 'costPrice');
                                }
                              }}
                              sx={{ ml: 1 }}
                            />
                          </HeaderCellWithActions>
                          <HeaderCellWithActions>
                            Precio de Venta ({APP_CURRENCY.symbol})
                            <FlagSwitch
                              checked={editableColumns[group.productCode]?.salePrice ?? false}
                              onChange={(e, checked) => {
                                if (
                                  checked !==
                                  (editableColumns[group.productCode]?.salePrice ?? false)
                                ) {
                                  toggleColumnEditable(group.productCode, 'salePrice');
                                }
                              }}
                              sx={{ ml: 1 }}
                            />
                          </HeaderCellWithActions>
                          <HeaderCell>Margen (%)</HeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.units.map((unit, unitIndex) => {
                          const globalIndex = getGlobalUnitIndex(unit);
                          const margin = calculateMargin(
                            unit.costs.costPrice,
                            unit.costs.salePrice,
                          );

                          return (
                            <TableRow key={unit.id}>
                              <BodyCell
                                style={{
                                  width: '38px',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                }}
                              >
                                {unitIndex + 1}
                              </BodyCell>
                              <BodyCell>
                                <DecimalInputField
                                  value={unit.costs.costPrice}
                                  onChange={(nextValue) =>
                                    handleUnitChange(globalIndex, 'costs', 'costPrice', nextValue)
                                  }
                                  min={0.01}
                                  max={999999.99}
                                  disabled={
                                    !(editableColumns[unit.productCode]?.costPrice ?? false)
                                  }
                                />
                              </BodyCell>
                              <BodyCell>
                                <DecimalInputField
                                  value={unit.costs.salePrice}
                                  onChange={(nextValue) =>
                                    handleUnitChange(globalIndex, 'costs', 'salePrice', nextValue)
                                  }
                                  min={0.01}
                                  max={999999.99}
                                  disabled={
                                    !(editableColumns[unit.productCode]?.salePrice ?? false)
                                  }
                                />
                              </BodyCell>
                              <BodyCell>
                                <span
                                  style={{
                                    color:
                                      parseFloat(margin) > 30
                                        ? '#059669'
                                        : parseFloat(margin) < 15
                                          ? '#dc2626'
                                          : '#6b7280',
                                  }}
                                >
                                  {margin}%
                                </span>
                              </BodyCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </ProductCard>
        ))}
      </ProductGroupsContainer>
    </Container>
  );
};

export default ProductPropertiesStep;
